import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Hash, Play, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type Run = {
  id: string;
  tool_name: string;
  tool_id: string;
  status: string;
  duration_ms: number | null;
  hash: string;
  input: any;
  output: any;
  created_at: string;
};

async function sha512Hex(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-512", buf);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export function Sandbox() {
  const { user } = useAuth();
  const [runs, setRuns] = useState<Run[]>([]);
  const [tool, setTool] = useState("http.get");
  const [input, setInput] = useState('{"url":"https://example.com"}');
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("sandbox_runs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100)
      .then(({ data }) => setRuns((data as Run[]) ?? []));

    const ch = supabase
      .channel("sandbox_runs_rt")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "sandbox_runs", filter: `user_id=eq.${user.id}` }, (payload) => {
        setRuns((prev) => [payload.new as Run, ...prev].slice(0, 100));
      })
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [user]);

  const exec = async () => {
    if (!user) return toast.error("Faça login");
    setRunning(true);
    const t0 = performance.now();
    let parsed: any = {};
    try { parsed = JSON.parse(input); } catch { /* allow plain */ parsed = { raw: input }; }

    let output: any;
    let status: "ok" | "error" = "ok";
    try {
      // Execução real conforme o tool. Exemplos suportados out-of-the-box: http.get, http.post, json.parse, time.now
      if (tool === "http.get" && parsed.url) {
        const r = await fetch(parsed.url, { method: "GET" });
        output = { status: r.status, ok: r.ok, text: (await r.text()).slice(0, 4000) };
      } else if (tool === "http.post" && parsed.url) {
        const r = await fetch(parsed.url, {
          method: "POST",
          headers: parsed.headers ?? { "Content-Type": "application/json" },
          body: typeof parsed.body === "string" ? parsed.body : JSON.stringify(parsed.body ?? {}),
        });
        output = { status: r.status, ok: r.ok, text: (await r.text()).slice(0, 4000) };
      } else if (tool === "time.now") {
        output = { iso: new Date().toISOString(), ms: Date.now() };
      } else if (tool === "json.parse") {
        output = { parsed };
      } else {
        // fallback: ecoa input
        output = { echo: parsed, note: "tool desconhecido — registrado para auditoria" };
      }
    } catch (e: any) {
      status = "error";
      output = { error: e?.message ?? String(e) };
    }

    const ms = Math.round(performance.now() - t0);
    const hash = await sha512Hex(JSON.stringify({ tool, input: parsed, output, ts: Date.now() }));

    const { error } = await supabase.from("sandbox_runs").insert({
      user_id: user.id,
      tool_id: tool,
      tool_name: tool,
      input: parsed,
      output,
      status,
      duration_ms: ms,
      hash,
    });

    setRunning(false);
    if (error) toast.error("Falha ao registrar audit: " + error.message);
    else toast.success(`Sandbox ${status} · ${ms}ms · sha512:${hash.slice(0, 8)}`);
  };

  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-primary" /> Sandbox
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Execução real · audit imutável SHA-512 · persistido com RLS por usuário
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4 space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Execute</div>
          <select
            value={tool}
            onChange={(e) => setTool(e.target.value)}
            className="w-full h-9 px-2 rounded bg-surface-2 border border-border text-sm font-mono"
          >
            <option value="http.get">http.get</option>
            <option value="http.post">http.post</option>
            <option value="json.parse">json.parse</option>
            <option value="time.now">time.now</option>
          </select>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={6}
            className="w-full px-2 py-2 rounded bg-surface-2 border border-border text-xs font-mono outline-none focus:border-primary/60"
          />
          <Button onClick={exec} disabled={running} size="sm" className="bg-gradient-neural text-primary-foreground border-0 gap-1.5">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {running ? "Running..." : "Run in sandbox"}
          </Button>
        </div>

        <div className="glass rounded-xl p-4">
          <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Audit Trail · {runs.length}</div>
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {runs.map((r) => (
              <div key={r.id} className="text-[11px] font-mono p-2 rounded bg-surface-2/60 border border-border">
                <div className="flex items-center gap-2">
                  <span className={`status-dot ${r.status === "ok" ? "text-success" : "text-destructive"}`} />
                  <span className="font-semibold">{r.tool_name}</span>
                  <span className="ml-auto text-muted-foreground">{r.duration_ms}ms</span>
                </div>
                <div className="flex items-center gap-1 mt-1 text-muted-foreground truncate">
                  <Hash className="w-2.5 h-2.5" /> sha512:{r.hash.slice(0, 24)}…
                </div>
              </div>
            ))}
            {runs.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-8">No runs yet. Execute para gerar trilha imutável.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
